import type { TFunction } from 'i18next';
import askPlato from '../../../lib/i18n/locales/en/ask-plato.json' with { type: 'json' };
import type { AiChatToolName } from '../../../server/ai-chat/tool-names.ts';
import type { AskPlatoMessagePart } from '../hooks/use-ask-plato-chat.ts';

type AskPlatoKey = keyof typeof askPlato;

/**
 * Caption keys per tool: `running` while the call is in flight, `done` once it
 * settled, and `search` for a settled call that ran with a search term. Typed
 * against the server's tool list, so a new tool fails to compile here until
 * it has captions.
 */
const captionKeys: Record<AiChatToolName, { running: AskPlatoKey; done: AskPlatoKey; search?: AskPlatoKey }> =
    {
        find_plato_page: { running: 'tool__find_plato_page__running', done: 'tool__find_plato_page__done' },
        get_customer_details: {
            running: 'tool__get_customer_details__running',
            done: 'tool__get_customer_details__done',
        },
        get_customer_financials: {
            running: 'tool__get_customer_financials__running',
            done: 'tool__get_customer_financials__done',
        },
        get_customer_notes: {
            running: 'tool__get_customer_notes__running',
            done: 'tool__get_customer_notes__done',
            search: 'tool__get_customer_notes__done_search',
        },
        get_customer_segment_aggregates: {
            running: 'tool__get_customer_segment_aggregates__running',
            done: 'tool__get_customer_segment_aggregates__done',
        },
        get_daily_order_intake: {
            running: 'tool__get_daily_order_intake__running',
            done: 'tool__get_daily_order_intake__done',
        },
        get_data_freshness: {
            running: 'tool__get_data_freshness__running',
            done: 'tool__get_data_freshness__done',
        },
        get_monthly_trend: {
            running: 'tool__get_monthly_trend__running',
            done: 'tool__get_monthly_trend__done',
        },
        get_note: { running: 'tool__get_note__running', done: 'tool__get_note__done' },
        get_pinned_customer_notes: {
            running: 'tool__get_pinned_customer_notes__running',
            done: 'tool__get_pinned_customer_notes__done',
        },
        get_quotes_orders_aggregates: {
            running: 'tool__get_quotes_orders_aggregates__running',
            done: 'tool__get_quotes_orders_aggregates__done',
        },
        get_revenue_by_year: {
            running: 'tool__get_revenue_by_year__running',
            done: 'tool__get_revenue_by_year__done',
        },
        get_sales_summary: {
            running: 'tool__get_sales_summary__running',
            done: 'tool__get_sales_summary__done',
        },
        get_sales_totals: {
            running: 'tool__get_sales_totals__running',
            done: 'tool__get_sales_totals__done',
        },
        get_team_members: {
            running: 'tool__get_team_members__running',
            done: 'tool__get_team_members__done',
        },
        get_team_overview: {
            running: 'tool__get_team_overview__running',
            done: 'tool__get_team_overview__done',
            search: 'tool__get_team_overview__done_search',
        },
        get_team_stats: {
            running: 'tool__get_team_stats__running',
            done: 'tool__get_team_stats__done',
            search: 'tool__get_team_stats__done_search',
        },
        get_top_categories: {
            running: 'tool__get_top_categories__running',
            done: 'tool__get_top_categories__done',
            search: 'tool__get_top_categories__done_search',
        },
        get_top_clerks: {
            running: 'tool__get_top_clerks__running',
            done: 'tool__get_top_clerks__done',
            search: 'tool__get_top_clerks__done_search',
        },
        get_top_customers: {
            running: 'tool__get_top_customers__running',
            done: 'tool__get_top_customers__done',
            search: 'tool__get_top_customers__done_search',
        },
        get_top_industries: {
            running: 'tool__get_top_industries__running',
            done: 'tool__get_top_industries__done',
            search: 'tool__get_top_industries__done_search',
        },
        get_top_product_groups: {
            running: 'tool__get_top_product_groups__running',
            done: 'tool__get_top_product_groups__done',
            search: 'tool__get_top_product_groups__done_search',
        },
        get_top_products: {
            running: 'tool__get_top_products__running',
            done: 'tool__get_top_products__done',
            search: 'tool__get_top_products__done_search',
        },
        get_top_sales_reps: {
            running: 'tool__get_top_sales_reps__running',
            done: 'tool__get_top_sales_reps__done',
            search: 'tool__get_top_sales_reps__done_search',
        },
        get_top_storage_locations: {
            running: 'tool__get_top_storage_locations__running',
            done: 'tool__get_top_storage_locations__done',
            search: 'tool__get_top_storage_locations__done_search',
        },
        get_top_suppliers: {
            running: 'tool__get_top_suppliers__running',
            done: 'tool__get_top_suppliers__done',
            search: 'tool__get_top_suppliers__done_search',
        },
    };

function isKnownTool(name: string): name is AiChatToolName {
    return Object.hasOwn(captionKeys, name);
}

/**
 * The caption of one tool-call row. A tool the client does not know (renamed
 * or removed since the message was stored) gets the generic captions.
 */
export function toolCallCaption(
    part: Extract<AskPlatoMessagePart, { type: 'toolCall' }>,
    t: TFunction<'ask-plato'>,
): string {
    const keys = isKnownTool(part.name) ? captionKeys[part.name] : undefined;
    switch (part.status) {
        case 'running':
            return t(keys?.running ?? 'tool__generic__running');
        case 'failed':
            return t('tool__failed');
        case 'interrupted':
            return t('tool__interrupted');
        case 'done':
            if (part.search !== undefined && keys?.search !== undefined) {
                return t(keys.search, { search: part.search });
            }
            return t(keys?.done ?? 'tool__generic__done');
    }
}
